import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Wrench, Plus, Trash2, Search, ShieldCheck, ShieldAlert, History, Upload, Check, X, AlertTriangle, Pencil, Save, Wand2 } from "lucide-react";
import SearchableSelect from "@/components/SearchableSelect";

const EMPTY_FORM = { customer_id: "", marca: "", modello: "", matricola: "", targa: "", ore_lavoro: 0, data_acquisto: "", garanzia_scadenza: "", note: "" };

export default function MachinesPage() {
  const [sp] = useSearchParams();
  const preCust = sp.get("customer");
  const [items, setItems] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [q, setQ] = useState("");
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState(null); // machine being edited or null
  const [importPreview, setImportPreview] = useState(null); // { file, summary }
  const [importing, setImporting] = useState(false);
  const [selected, setSelected] = useState(() => new Set());
  const [bulkMarca, setBulkMarca] = useState("");
  const [form, setForm] = useState({ ...EMPTY_FORM, customer_id: preCust || "" });

  const load = async () => {
    const params = {};
    if (preCust) params.customer_id = preCust;
    if (q) params.q = q;
    const { data } = await api.get("/machines", { params });
    setItems(data);
  };
  useEffect(() => {
    load();
    api.get("/customers").then(r => setCustomers(r.data));
    /* eslint-disable-next-line */
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/machines", { ...form, ore_lavoro: parseFloat(form.ore_lavoro)||0 });
      toast.success("Macchina aggiunta"); setCreating(false);
      setForm({ ...EMPTY_FORM, customer_id: preCust || "" });
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const del = async (id) => { if (!window.confirm("Eliminare macchina?")) return; await api.delete(`/machines/${id}`); load(); };

  const startEdit = (m) => {
    setEditing({
      id: m.id,
      customer_id: m.customer_id || "",
      marca: m.marca || "",
      modello: m.modello || "",
      matricola: m.matricola || "",
      targa: m.targa || "",
      ore_lavoro: m.ore_lavoro || 0,
      data_acquisto: m.data_acquisto || "",
      garanzia_scadenza: m.garanzia_scadenza || "",
      note: m.note || "",
    });
  };

  const saveEdit = async (e) => {
    e.preventDefault();
    const { id, ...body } = editing;
    try {
      await api.put(`/machines/${id}`, { ...body, ore_lavoro: parseFloat(body.ore_lavoro)||0 });
      toast.success("Macchina aggiornata");
      setEditing(null);
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const runRename = async () => {
    const ids = Array.from(selected);
    const marca = (bulkMarca || "").trim();
    if (ids.length === 0) { toast.error("Seleziona almeno una macchina"); return; }
    if (!marca) { toast.error("Scrivi la nuova marca"); return; }
    if (!window.confirm(`Cambiare marca a ${ids.length} macchin${ids.length===1?"a":"e"} → "${marca}"?`)) return;
    try {
      const { data } = await api.post("/machines/bulk-set-marca", { machine_ids: ids, marca });
      toast.success(`${data.modified} macchine aggiornate`);
      setSelected(new Set());
      setBulkMarca("");
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const toggleOne = (id) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const allSelected = items.length > 0 && items.every(m => selected.has(m.id));
  const toggleAll = () => {
    setSelected(prev => {
      if (allSelected) return new Set();
      const next = new Set(prev);
      items.forEach(m => next.add(m.id));
      return next;
    });
  };

  const isWarranty = (m) => m.garanzia_scadenza && new Date(m.garanzia_scadenza) >= new Date();

  // Import XLSX (dry-run preview)
  const onImportFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setImporting(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/machines/import-xlsx?dry_run=true", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setImportPreview({ file: f, summary: data });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Errore lettura file");
    } finally {
      setImporting(false);
      e.target.value = "";
    }
  };

  const confirmImport = async () => {
    if (!importPreview?.file) return;
    setImporting(true);
    try {
      const fd = new FormData();
      fd.append("file", importPreview.file);
      const { data } = await api.post("/machines/import-xlsx?dry_run=false", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Import: ${data.created_machines} macchine + ${data.created_customers} clienti creati. ${data.duplicates_skipped} duplicati saltati.`);
      setImportPreview(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Errore import");
    } finally {
      setImporting(false);
    }
  };

  return (
    <div className="p-4 md:p-8 max-w-6xl mx-auto">
      <div className="flex items-end justify-between mb-6 flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-zinc-500 mb-2">Parco macchine</div>
          <h1 className="font-display text-4xl font-black uppercase tracking-tight">Macchine</h1>
        </div>
        <button onClick={()=>setCreating(!creating)} className="gm-btn-primary h-11 px-5 rounded-sm flex items-center gap-2" data-testid="machine-new-button">
          <Plus size={16} /> Nuova macchina
        </button>
      </div>

      <div className="flex gap-2 mb-5 flex-wrap">
        <label className="h-11 px-4 border border-zinc-700 hover:border-[#4CAF50] text-zinc-300 hover:text-[#4CAF50] uppercase text-xs tracking-widest font-bold rounded-sm flex items-center gap-2 cursor-pointer" data-testid="machine-import-button">
          <Upload size={14} /> {importing && !importPreview ? "Analisi..." : "Importa XLSX (storico venduto)"}
          <input type="file" accept=".xlsx,.xls" className="hidden" onChange={onImportFile} disabled={importing} />
        </label>
      </div>

      {importPreview && (
        <div className="gm-card p-5 mb-5 border-[#4CAF50]/40" data-testid="machine-import-preview">
          <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
            <div>
              <div className="text-xs uppercase tracking-widest text-[#4CAF50] font-bold">Anteprima import macchine</div>
              <h3 className="font-display text-xl font-bold uppercase">{importPreview.file.name}</h3>
            </div>
            <button onClick={()=>setImportPreview(null)} className="text-zinc-400 hover:text-white p-1"><X size={20}/></button>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4">
            <div className="gm-card p-3">
              <div className="text-[10px] uppercase tracking-widest text-zinc-500">Righe totali</div>
              <div className="text-2xl font-mono-data font-bold">{importPreview.summary.total_rows}</div>
            </div>
            <div className="gm-card p-3">
              <div className="text-[10px] uppercase tracking-widest text-zinc-500">Macchine da creare</div>
              <div className="text-2xl font-mono-data font-bold text-[#4CAF50]">{importPreview.summary.machines_to_create}</div>
            </div>
            <div className="gm-card p-3">
              <div className="text-[10px] uppercase tracking-widest text-zinc-500">Clienti già in DB</div>
              <div className="text-2xl font-mono-data font-bold">{importPreview.summary.customers_matched}</div>
            </div>
            <div className="gm-card p-3">
              <div className="text-[10px] uppercase tracking-widest text-zinc-500">Nuovi clienti</div>
              <div className="text-2xl font-mono-data font-bold text-yellow-400">{importPreview.summary.customers_to_create}</div>
            </div>
            <div className="gm-card p-3">
              <div className="text-[10px] uppercase tracking-widest text-zinc-500">Duplicati saltati</div>
              <div className="text-2xl font-mono-data font-bold text-zinc-500">{importPreview.summary.duplicates_skipped}</div>
            </div>
          </div>

          {importPreview.summary.no_customer_rows > 0 && (
            <div className="mb-3 flex items-center gap-2 text-xs text-yellow-400 bg-yellow-500/5 border border-yellow-500/30 p-2 rounded-sm">
              <AlertTriangle size={14}/> {importPreview.summary.no_customer_rows} righe senza cliente verranno saltate.
            </div>
          )}

          {importPreview.summary.customers_to_create > 0 && (
            <details className="mb-3 border border-zinc-800 p-3 rounded-sm">
              <summary className="cursor-pointer text-xs uppercase tracking-widest text-yellow-400 font-bold">
                Nuovi clienti da creare ({importPreview.summary.customers_to_create})
              </summary>
              <div className="mt-2 flex flex-wrap gap-2">
                {(importPreview.summary.new_customer_names || []).map((n,i) => (
                  <span key={i} className="gm-badge gm-badge-muted text-[11px]">{n}</span>
                ))}
              </div>
            </details>
          )}

          <div className="border-t border-zinc-800 pt-3 mb-3">
            <div className="text-xs uppercase tracking-widest text-zinc-500 mb-2">Anteprima prime 20 righe</div>
            <div className="overflow-x-auto">
              <table className="gm-table text-xs">
                <thead><tr><th>Foglio</th><th>Marca</th><th>Modello</th><th>Matricola</th><th>Cliente</th><th>Status</th></tr></thead>
                <tbody>
                  {(importPreview.summary.rows_preview || []).map((r,i) => (
                    <tr key={i}>
                      <td className="text-zinc-500">{r.sheet}</td>
                      <td>{r.marca}</td>
                      <td>{r.modello || "—"}</td>
                      <td className="font-mono-data text-[#4CAF50]">{r.matricola}</td>
                      <td>{r.cliente || <span className="text-zinc-600">—</span>}</td>
                      <td>
                        {r.status === "duplicate_skipped" && <span className="gm-badge gm-badge-muted">duplicato</span>}
                        {r.status === "no_customer" && <span className="gm-badge" style={{color:"#f59e0b"}}>no cliente</span>}
                        {r.status === "ok" && (r.matched
                          ? <span className="gm-badge gm-badge-success">match</span>
                          : <span className="gm-badge" style={{color:"#f59e0b"}}>nuovo cliente</span>)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="flex gap-2 flex-wrap">
            <button onClick={confirmImport} disabled={importing || importPreview.summary.machines_to_create === 0} className="gm-btn-primary h-11 px-5 rounded-sm flex items-center gap-2" data-testid="machine-import-confirm">
              <Check size={16} /> {importing ? "Import in corso..." : `Importa ${importPreview.summary.machines_to_create} macchine + ${importPreview.summary.customers_to_create} clienti`}
            </button>
            <button onClick={()=>setImportPreview(null)} className="h-11 px-5 border border-zinc-700 uppercase text-xs tracking-widest font-bold" data-testid="machine-import-cancel">Annulla</button>
          </div>
        </div>
      )}

      {creating && (
        <form onSubmit={submit} className="gm-card p-5 mb-5 grid grid-cols-1 md:grid-cols-3 gap-3">
          <div className="md:col-span-3">
            <SearchableSelect
              required
              testId="machine-customer"
              value={form.customer_id}
              onChange={(v)=>setForm(s=>({...s, customer_id: v}))}
              placeholder="— Cliente * (scrivi per cercare)"
              options={customers.map(c => ({ value: c.id, label: c.ragione_sociale, subLabel: c.piva || c.referente || "" }))}
            />
          </div>
          <input required placeholder="Marca *" value={form.marca} onChange={e=>setForm(s=>({...s,marca:e.target.value}))} className="gm-input" />
          <input required placeholder="Modello *" value={form.modello} onChange={e=>setForm(s=>({...s,modello:e.target.value}))} className="gm-input" />
          <input required placeholder="Matricola *" value={form.matricola} onChange={e=>setForm(s=>({...s,matricola:e.target.value}))} className="gm-input font-mono-data" />
          <input placeholder="Targa" value={form.targa} onChange={e=>setForm(s=>({...s,targa:e.target.value}))} className="gm-input font-mono-data" />
          <input type="number" placeholder="Ore lavoro" value={form.ore_lavoro} onChange={e=>setForm(s=>({...s,ore_lavoro:e.target.value}))} className="gm-input font-mono-data" />
          <div>
            <label className="text-[10px] uppercase tracking-widest text-zinc-500">Data acquisto</label>
            <input type="date" value={form.data_acquisto} onChange={e=>setForm(s=>({...s,data_acquisto:e.target.value}))} className="gm-input" />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest text-zinc-500">Garanzia fino al</label>
            <input type="date" value={form.garanzia_scadenza} onChange={e=>setForm(s=>({...s,garanzia_scadenza:e.target.value}))} className="gm-input" />
          </div>
          <input placeholder="Note" value={form.note} onChange={e=>setForm(s=>({...s,note:e.target.value}))} className="gm-input" />
          <button type="submit" className="gm-btn-primary h-11 rounded-sm md:col-span-3" data-testid="machine-submit">Crea macchina</button>
        </form>
      )}

      <div className="flex gap-3 mb-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" size={18} />
          <input value={q} onChange={(e)=>setQ(e.target.value)} onKeyDown={(e)=>e.key==='Enter'&&load()} placeholder="Cerca per marca, modello, matricola..." className="gm-input pl-11" data-testid="machine-search" />
        </div>
        <button onClick={load} className="h-12 px-6 border border-zinc-700 hover:border-white uppercase text-xs tracking-widest font-bold">Cerca</button>
      </div>

      {selected.size > 0 && (
        <div className="gm-card p-3 mb-3 border-[#4CAF50]/40 bg-[#4CAF50]/5 sticky top-2 z-10" data-testid="machine-bulk-bar">
          <div className="flex items-center gap-3 flex-wrap">
            <div className="flex items-center gap-2">
              <Wand2 size={16} className="text-[#4CAF50]" />
              <span className="text-xs uppercase tracking-widest font-bold text-[#4CAF50]">{selected.size} selezionat{selected.size===1?"a":"e"}</span>
            </div>
            <div className="flex-1 flex items-center gap-2 min-w-[240px]">
              <label className="text-[10px] uppercase tracking-widest text-zinc-400 whitespace-nowrap">Nuova marca:</label>
              <input
                value={bulkMarca}
                onChange={e=>setBulkMarca(e.target.value)}
                placeholder="es. WACKER NEUSON"
                className="gm-input flex-1"
                data-testid="bulk-marca-input"
                onKeyDown={(e)=>{ if(e.key==="Enter") runRename(); }}
              />
            </div>
            <button onClick={runRename} className="gm-btn-primary h-11 px-5 rounded-sm flex items-center gap-2" data-testid="bulk-marca-apply">
              <Check size={16} /> Cambia marca
            </button>
            <button onClick={()=>setSelected(new Set())} className="h-11 px-4 border border-zinc-700 hover:border-white uppercase text-xs tracking-widest font-bold" data-testid="bulk-marca-clear">Deseleziona</button>
          </div>
        </div>
      )}

      <div className="gm-card overflow-x-auto">
        <table className="gm-table">
          <thead><tr>
            <th className="w-10">
              <input
                type="checkbox"
                checked={allSelected}
                onChange={toggleAll}
                className="w-4 h-4 accent-[#4CAF50] cursor-pointer"
                data-testid="machine-select-all"
                aria-label="Seleziona tutte"
              />
            </th>
            <th>Cliente</th><th>Marca/Modello</th><th>Matricola</th><th>Targa</th><th className="text-right">Ore</th><th>Garanzia</th><th></th>
          </tr></thead>
          <tbody>
            {items.length === 0 && (<tr><td colSpan="8" className="text-center text-zinc-500 py-8">Nessuna macchina</td></tr>)}
            {items.map(m => (
              <tr key={m.id} data-testid="machine-row" className={selected.has(m.id) ? "bg-[#4CAF50]/5" : ""}>
                <td>
                  <input
                    type="checkbox"
                    checked={selected.has(m.id)}
                    onChange={()=>toggleOne(m.id)}
                    className="w-4 h-4 accent-[#4CAF50] cursor-pointer"
                    data-testid="machine-select-row"
                    aria-label={`Seleziona ${m.marca} ${m.modello}`}
                  />
                </td>
                <td className="text-sm">{m.customer_name || "—"}</td>
                <td><div className="font-semibold">{m.marca} {m.modello}</div></td>
                <td className="font-mono-data text-[#4CAF50]">{m.matricola}</td>
                <td className="font-mono-data text-zinc-400">{m.targa || "—"}</td>
                <td className="text-right font-mono-data">{m.ore_lavoro || 0}h</td>
                <td>
                  {isWarranty(m) ? <span className="gm-badge gm-badge-success"><ShieldCheck size={12} className="inline mr-1" />Attiva</span>
                   : <span className="gm-badge gm-badge-muted"><ShieldAlert size={12} className="inline mr-1" />Scaduta</span>}
                </td>
                <td className="text-right space-x-1 whitespace-nowrap">
                  <Link to={`/macchine/${m.id}/storico`} className="text-xs uppercase tracking-widest text-[#4CAF50] hover:underline inline-flex items-center gap-1"><History size={12}/> Storico</Link>
                  <button onClick={()=>startEdit(m)} className="text-zinc-300 hover:text-[#4CAF50] hover:bg-[#4CAF50]/10 p-1.5 inline-block" data-testid="machine-edit-btn" title="Modifica"><Pencil size={14}/></button>
                  <button onClick={()=>del(m.id)} className="text-[#FF3B30] hover:bg-[#FF3B30]/10 p-1.5 inline-block" title="Elimina"><Trash2 size={14}/></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Modal Modifica Macchina */}
      {editing && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-start md:items-center justify-center p-4 overflow-y-auto" data-testid="machine-edit-modal">
          <form onSubmit={saveEdit} className="gm-card p-5 w-full max-w-2xl my-8 grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="md:col-span-2 flex items-center justify-between mb-2">
              <div>
                <div className="text-xs uppercase tracking-widest text-[#4CAF50] font-bold">Modifica macchina</div>
                <h3 className="font-display text-xl font-black uppercase">{editing.marca} {editing.modello}</h3>
              </div>
              <button type="button" onClick={()=>setEditing(null)} className="text-zinc-400 hover:text-white p-1"><X size={20}/></button>
            </div>
            <div className="md:col-span-2">
              <label className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1 block">Cliente *</label>
              <SearchableSelect
                required
                testId="edit-machine-customer"
                value={editing.customer_id}
                onChange={(v)=>setEditing(s=>({...s, customer_id: v}))}
                placeholder="— Cliente * (scrivi per cercare)"
                options={customers.map(c => ({ value: c.id, label: c.ragione_sociale, subLabel: c.piva || c.referente || "" }))}
              />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest text-zinc-500">Marca *</label>
              <input required value={editing.marca} onChange={e=>setEditing(s=>({...s,marca:e.target.value}))} className="gm-input" data-testid="edit-machine-marca" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest text-zinc-500">Modello *</label>
              <input required value={editing.modello} onChange={e=>setEditing(s=>({...s,modello:e.target.value}))} className="gm-input" data-testid="edit-machine-modello" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest text-zinc-500">Matricola *</label>
              <input required value={editing.matricola} onChange={e=>setEditing(s=>({...s,matricola:e.target.value}))} className="gm-input font-mono-data" data-testid="edit-machine-matricola" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest text-zinc-500">Targa</label>
              <input value={editing.targa} onChange={e=>setEditing(s=>({...s,targa:e.target.value}))} className="gm-input font-mono-data" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest text-zinc-500">Ore lavoro</label>
              <input type="number" value={editing.ore_lavoro} onChange={e=>setEditing(s=>({...s,ore_lavoro:e.target.value}))} className="gm-input font-mono-data" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest text-zinc-500">Data acquisto</label>
              <input type="date" value={editing.data_acquisto} onChange={e=>setEditing(s=>({...s,data_acquisto:e.target.value}))} className="gm-input" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest text-zinc-500">Garanzia fino al</label>
              <input type="date" value={editing.garanzia_scadenza} onChange={e=>setEditing(s=>({...s,garanzia_scadenza:e.target.value}))} className="gm-input" />
            </div>
            <div className="md:col-span-2">
              <label className="text-[10px] uppercase tracking-widest text-zinc-500">Note</label>
              <input value={editing.note} onChange={e=>setEditing(s=>({...s,note:e.target.value}))} className="gm-input" />
            </div>
            <div className="md:col-span-2 flex gap-2 pt-2 border-t border-zinc-800">
              <button type="submit" className="gm-btn-primary h-11 px-5 rounded-sm flex items-center gap-2" data-testid="edit-machine-save">
                <Save size={16} /> Salva modifiche
              </button>
              <button type="button" onClick={()=>setEditing(null)} className="h-11 px-5 border border-zinc-700 uppercase text-xs tracking-widest font-bold">Annulla</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
